import { Alert, Button, Collapse, Group, Loader, Stack, Title, toast } from '@common/ui'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Papa from 'papaparse'
import { com, kotlin } from '@eidu/web'
import useEntityTypes, { EMPTY_ENTITY_TYPES } from '../../io/useEntityTypes'
import useEntityType from '../../io/useEntityType'
import { useEntitiesByIds, useFetchEntities } from '../../io/useEntities'
import { useCreateEntities } from '../../io/useEntityMutations'
import extractFieldsAndData from '../../domain/entity/extractFieldsAndData'
import { ExistingFieldData, fieldDataFromExistingField } from '../../domain/entity/FieldData'
import createValidatedFieldValues from '../../domain/entity/createValidatedFieldValues'
import { BatchFieldErrors } from '../../domain/entity/FieldValueUtil'
import UploadEntityDataGrid from '../../components/data/UploadEntityDataGrid'
import FileInput from '../../components/input/FileInput'
import pageSizeOptions from '../../util/pagination/pageSizeOptions'
import PaginationModel from '../../util/pagination/PaginationModel'
import { isNotUndefinedOrNull } from '../../util/predicates'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import FieldId = com.eidu.entity.field.FieldId
import FieldType = com.eidu.entity.field.FieldType
import FieldValue = com.eidu.entity.field.FieldValue
import EntityId = com.eidu.entity.EntityId
import entityIdFromStringOrNull = com.eidu.entity.entityIdFromStringOrNull
import EntityToCreate = com.eidu.entity.api.entities.EntityToCreate
import KtMap = kotlin.collections.KtMap
import Success = com.eidu.sharedlib.util.Success
import Failure = com.eidu.sharedlib.util.Failure

type ImportEntitiesPageProps = {
  entityTypeId: EntityTypeId
}

type ImportState = {
  existingFields: readonly ExistingFieldData[]
  fields: readonly ExistingFieldData[]
  data: readonly (readonly string[])[]
  fieldValues: readonly Map<FieldId, FieldValue>[]
  validationErrors?: BatchFieldErrors
}

const hasDuplicateFieldNames = (rows: readonly (readonly string[])[]) => {
  const header = rows[0]
  const occurrences = new Map<string, number>()
  header.forEach((name) => {
    occurrences.set(name, (occurrences.get(name) ?? 0) + 1)
  })
  return Array.from(occurrences.values()).some((count) => count > 1)
}

const extractPopulatedFieldsInOrder = (fields: readonly ExistingFieldData[]): readonly ExistingFieldData[] =>
  fields
    .filter((field): field is ExistingFieldData & { columnIndex: number } => field.columnIndex !== undefined)
    .sort((a, b) => a.columnIndex! - b.columnIndex!)

const prepareEntitiesPayload = (entityTypeId: EntityTypeId, fieldValues: readonly Map<FieldId, FieldValue>[]) =>
  fieldValues.map(
    (value) =>
      new EntityToCreate(entityTypeId, KtMap.fromJsMap(value), KtMap.fromJsMap(new Map()), KtMap.fromJsMap(new Map()))
  )

const ImportEntitiesPage = ({ entityTypeId }: ImportEntitiesPageProps) => {
  const navigate = useNavigate()

  const { entityTypes, error: typesError } = useEntityTypes()
  const { entityType, error: typeError } = useEntityType(entityTypeId)
  const composedError = typesError ?? typeError
  const repositoryError = composedError ? String(composedError) : undefined
  const entityTypesMap: ReadonlyMap<EntityTypeId, EntityType> = entityTypes ?? EMPTY_ENTITY_TYPES

  const fetchEntities = useFetchEntities()
  const createEntitiesMutation = useCreateEntities()

  const getEntityTypeIds = useMemo(
    () => async (ids: readonly EntityId[]) => {
      const entitiesByIdMap = await fetchEntities(ids)
      return new Map(
        Array.from(entitiesByIdMap.entries())
          .filter(([, entity]) => entity !== null)
          .map(([id, entity]): [EntityId, EntityTypeId] => [
            id,
            (entity as { entity: { typeId: EntityTypeId } }).entity.typeId,
          ])
      )
    },
    [fetchEntities]
  )

  const [state, setState] = useState<ImportState | undefined>()
  const [parseError, setParseError] = useState<string | undefined>()
  const [submitting, setSubmitting] = useState(false)
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    pageSize: pageSizeOptions.small,
    page: 0,
  })

  useEffect(() => {
    if (entityType) {
      const fields = entityType.fields
        .asJsReadonlyArrayView()
        .map((field) => ({ ...fieldDataFromExistingField(field, undefined), editableName: false }))
      setState((previous) => ({
        existingFields: fields,
        fields: previous?.fields ?? [],
        data: previous?.data ?? [],
        fieldValues: previous?.fieldValues ?? [],
        validationErrors: previous?.validationErrors,
      }))
    }
  }, [entityType])

  const onFileSelected = (file: File) => {
    if (!state?.existingFields) return
    setParseError(undefined)
    Papa.parse<string[]>(file, {
      skipEmptyLines: 'greedy',
      complete: async (result) => {
        const rows = result.data as readonly (readonly string[])[]
        if (rows.length === 0) {
          setParseError('The CSV file is empty.')
          return
        }
        if (hasDuplicateFieldNames(rows)) {
          setParseError('The CSV header contains duplicate field names.')
          return
        }
        const { fieldData, data } = extractFieldsAndData(state.existingFields, rows)
        const normalizedFieldData = fieldData.map((field) =>
          field.existing ? { ...field, editableName: false } : field
        )
        const newFields = normalizedFieldData.filter((field) => !field.existing)
        if (newFields.length > 0) {
          setParseError(
            `The CSV file contains unknown field names: ${newFields
              .map((field) => field.field.name)
              .join(', ')}. Please update the file to match the current type definition.`
          )
          return
        }
        const missingRequiredFields = normalizedFieldData.filter(
          (field) => field.existing && (field as ExistingFieldData).columnIndex === undefined && field.field.required
        )
        if (missingRequiredFields.length > 0) {
          setParseError(
            `The CSV file is missing required fields: ${missingRequiredFields
              .map((field) => field.field.name)
              .join(', ')}. Please update the file to match the current type definition.`
          )
          return
        }

        const orderedFields = normalizedFieldData.filter((field): field is ExistingFieldData => field.existing)

        try {
          const populatedFields = extractPopulatedFieldsInOrder(orderedFields)
          const columnMapping = populatedFields.map((field) => orderedFields.indexOf(field))
          if (columnMapping.some((index) => index < 0)) {
            setParseError('Failed to process the CSV file. Please try again.')
            return
          }
          const dataForValidation = data.map((row) => columnMapping.map((index) => row[index]!))

          const validationOutcome = await createValidatedFieldValues(
            dataForValidation,
            populatedFields,
            getEntityTypeIds
          )

          if (validationOutcome instanceof Success) {
            setState({
              existingFields: state.existingFields,
              fields: orderedFields,
              data,
              fieldValues: validationOutcome.value,
              validationErrors: undefined,
            })
          } else if (validationOutcome instanceof Failure) {
            setState({
              existingFields: state.existingFields,
              fields: orderedFields,
              data,
              fieldValues: [],
              validationErrors: validationOutcome.value,
            })
          } else {
            setParseError('Failed to validate the CSV file.')
          }
        } catch (error) {
          setParseError(String(error))
        }
      },
      error: () => setParseError('Failed to parse the CSV file. Please try again.'),
    })
  }

  const referencedEntityIds: EntityId[] = useMemo(() => {
    if (!state?.fields || !state?.data) return []
    return state.fields
      .filter((field) => field.field.type === FieldType.Reference && field.columnIndex !== undefined)
      .flatMap((field) =>
        state.data.map((row) => entityIdFromStringOrNull(row[field.columnIndex!])).filter(isNotUndefinedOrNull)
      )
  }, [state?.fields, state?.data])

  const { entities: relatedEntitiesMap } = useEntitiesByIds(referencedEntityIds)
  const relatedEntities = relatedEntitiesMap ?? new Map()

  const handleImport = async () => {
    if (!state || state.fieldValues.length === 0 || state.validationErrors) {
      toast.negative('Please fix the errors in the CSV file before importing.')
      return
    }
    setSubmitting(true)
    try {
      const entities = prepareEntitiesPayload(entityTypeId, state.fieldValues)
      await createEntitiesMutation.mutateAsync(entities)
      toast.positive('Import successful.')
      navigate(-1)
    } catch (_error) {
      toast.negative('Failed to import entities.')
    } finally {
      setSubmitting(false)
    }
  }

  const csvPresent = state?.data && state.data.length > 0

  return (
    <Stack gap="lg" p="lg">
      <Title order={2} data-testid="import-entities-title">
        {entityType ? `Import ${entityType.name}` : 'Import entities'}
      </Title>
      <FileInput
        required
        accept=".csv"
        onChange={(file) => {
          if (file) onFileSelected(file)
        }}
      />
      <Collapse expanded={!!parseError || !!repositoryError}>
        <Alert severity="negative">{parseError || repositoryError}</Alert>
      </Collapse>
      {csvPresent && state && (
        <>
          {state.validationErrors && (
            <Alert severity="negative">Found validation errors. Please correct the data before importing.</Alert>
          )}
          <div>
            <UploadEntityDataGrid
              data={state.data}
              fields={state.fields}
              referencedEntities={relatedEntities}
              referencedTypes={entityTypesMap}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
              validationErrors={state.validationErrors}
            />
          </div>
          <Group justify="flex-end">
            <Button
              onClick={handleImport}
              disabled={submitting || !!state.validationErrors}
              leftSection={submitting ? <Loader size="xs" /> : undefined}
              data-testid="import-entities-submit"
            >
              {submitting ? 'Importing…' : 'Import'}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  )
}

export default ImportEntitiesPage
